import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import AnalysisScene from "./AnalysisScene";
import { greetingName } from "./firstName";
import PortalAccess from "./PortalAccess";
import HairlineFigure from "./HairlineFigure";
import type { SlimLeadView } from "../OnboardingAPI";

/** A small check that draws itself: the arrival is confirmed, nothing is being processed. */
function Arrived() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16 text-foreground" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-testid="arrived-mark">
      <circle cx="32" cy="32" r="28" pathLength={1} className="onb-line" data-on={on ? "true" : "false"} />
      <path d="M20 33l8 8 16-18" pathLength={1} className="onb-line" data-on={on ? "true" : "false"} style={{ transitionDelay: "350ms" }} />
    </svg>
  );
}

/**
 * What becomes of the access after the submit, in one quiet line that is replaced softly: first "being made" (with a
 * spinner), then "on its way" once the server says the access mail went out - or, if that takes longer than the page
 * waits, a calm sentence that we get in touch. Nothing is claimed that the server has not said.
 */
function AccessLine({ sent, slow }: { sent: boolean; slow: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-7" aria-live="polite" data-testid="access-line">
      {sent ? (
        <p key="sent" className="onb-soft text-base leading-7 text-muted-foreground" data-testid="access-sent">
          {t("onboardingPage.status.accessSent")}
        </p>
      ) : slow ? (
        <p key="slow" className="onb-soft text-base leading-7 text-muted-foreground" data-testid="access-slow">
          {t("onboardingPage.status.accessSlow")}
        </p>
      ) : (
        <p key="pending" role="status" className="onb-soft flex items-center justify-center gap-2 text-base leading-7 text-muted-foreground" data-testid="access-pending">
          <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {t("onboardingPage.status.accessPending")}
        </p>
      )}
    </div>
  );
}

/**
 * Once the lead has left "open" this is the whole page: a greeting with the first name, the status, and an
 * honest note. "received" is a quiet confirmation (no analysis is claimed), "analyzing" shows the six areas
 * the data is looked at in, "report_sent" the finished house. No form, no file names, no answers, no company
 * profile, no steps - the page does not hold any of that any more.
 */
export default function ClosedScene({ slim, token, accessSlow = false }: { slim: SlimLeadView; token?: string; accessSlow?: boolean }) {
  const { t } = useTranslation();
  const status = slim.customerStatus;
  const name = greetingName(slim.firstName);
  return (
    <div className="onb-card onb-rise relative mx-auto flex w-full max-w-[1120px] flex-col items-center justify-center gap-6 overflow-hidden rounded-3xl border border-border bg-card px-6 py-10 text-center shadow-sm sm:px-12" data-testid="closed-scene" data-status={status}>
      {status === "report_sent" && (
        <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
          <rect x="6" y="6" width="calc(100% - 12px)" height="calc(100% - 12px)" rx="22" fill="none" pathLength={1} strokeWidth="1.4" className="onb-run stroke-primary" />
        </svg>
      )}
      <div className="max-w-2xl space-y-2">
        <p className="text-sm text-muted-foreground">{name ? t("onboardingPage.welcome.hello", { name }) : t("onboardingPage.welcome.helloAnonymous")}</p>
        <h1 tabIndex={-1} data-step-heading className="text-3xl font-semibold tracking-tight outline-none [overflow-wrap:anywhere] sm:text-4xl">
          {t(`onboardingPage.status.${status}.title`)}
        </h1>
        <p role="status" className="text-base leading-7 text-muted-foreground">
          {t(`onboardingPage.status.${status}.text`)}
        </p>
        {/* from the submit on: the access is being made, then its mail is on its way - as the server says */}
        <AccessLine sent={slim.accessSent === true} slow={accessSlow} />
        {token && <PortalAccess token={token} slim={slim} />}
      </div>

      {status === "report_sent" ? (
        <div className="w-full max-w-sm">
          <HairlineFigure name="house" loop={false} />
        </div>
      ) : status === "analyzing" ? (
        <div className="w-full max-w-2xl">
          <AnalysisScene />
        </div>
      ) : (
        <Arrived />
      )}

      <p className="max-w-xl text-sm text-muted-foreground">{t("onboardingPage.status.contact")}</p>
    </div>
  );
}
